import { ValidationError } from '~/errors';
import { BaseRestRepository } from './baseRestRepository';
import { uploadSessionRepository } from './uploadSessionRepository';
import type { UploadFileOptions } from './uploadSessionRepository';
import type {
  CompanyDomainDto,
  CompanyDto,
  CompanyStatusDto,
  CompanySubUrlDto,
  EligibleDto,
  PortalSettingsDto,
  ResolveSelfManagedDto,
  SelfManagedFlagDto,
  UnassignedBankAccountDto,
  UpdateCompanyDto,
  UpdatePortalSettingsDto,
  ValidateDto,
} from '~/swagger/portal/Api';

const COMPANIES_URL = '/api/companies';

export const validateCompanySubUrl = (model: CompanySubUrlDto) => {
  if (typeof model?.url !== 'string' || model.url.length < 3) {
    throw new ValidationError('Invalid company sub url');
  }
};

export class CompaniesRepository extends BaseRestRepository {
  public async getCompanies(): Promise<CompanyDto> {
    const url = COMPANIES_URL;
    const response = await this.fetchWithAuth(url);
    return this.getJsonResponse(response);
  }

  public async updateSubUrl(model: CompanySubUrlDto | CompanyDomainDto): Promise<CompanyDto> {
    const url = `${COMPANIES_URL}/sub-url`;
    const response = await this.fetchWithAuth(url, {
      method: 'PUT',
      body: JSON.stringify(model),
    });
    return this.getJsonResponse(response);
  }

  public async updateCustomUrl(model: CompanyDomainDto | CompanySubUrlDto): Promise<CompanyDto> {
    const url = `${COMPANIES_URL}/custom-url`;
    const response = await this.fetchWithAuth(url, {
      method: 'POST',
      body: JSON.stringify(model),
    });
    return this.getJsonResponse(response);
  }

  public async validateSubUrl(model: CompanySubUrlDto): Promise<ValidateDto> {
    validateCompanySubUrl(model);
    const url = `${COMPANIES_URL}/sub-url/validate`;
    const response = await this.fetchWithAuth(url, {
      method: 'POST',
      body: JSON.stringify(model),
    });
    return this.getJsonResponse(response);
  }

  public async createCustomUrl(model: CompanySubUrlDto): Promise<CompanyDto> {
    validateCompanySubUrl(model);
    const url = `${COMPANIES_URL}/custom-url`;
    const response = await this.fetchWithAuth(url, {
      method: 'POST',
      body: JSON.stringify(model),
    });
    return this.getJsonResponse(response);
  }

  public async validateCustomUrl(model: CompanyDomainDto): Promise<ValidateDto> {
    // validateCompanySubUrl(model);
    const url = `${COMPANIES_URL}/custom-url/validate`;
    const response = await this.fetchWithAuth(url, {
      method: 'POST',
      body: JSON.stringify(model),
    });
    return this.getJsonResponse(response);
  }

  public async updateCompanyProfile(model: UpdateCompanyDto): Promise<CompanyDto> {
    const url = COMPANIES_URL;
    const response = await this.fetchWithAuth(url, {
      method: 'PUT',
      body: JSON.stringify(model),
    });
    return this.getJsonResponse(response);
  }

  public async getPortalSettings(): Promise<PortalSettingsDto> {
    const url = `${COMPANIES_URL}/portal-settings`;
    const response = await this.fetchWithAuth(url);
    return this.getJsonResponse(response);
  }

  public async uploadPortalSettingsFile(file: File, opts?: UploadFileOptions): Promise<string> {
    const [uploadSessionId] = await uploadSessionRepository.uploadFiles(`${COMPANIES_URL}/portal-settings/upload-sessions`, [file], opts);
    return uploadSessionId;
  }

  public async updatePortalSettings(model: UpdatePortalSettingsDto): Promise<PortalSettingsDto> {
    const url = `${COMPANIES_URL}/portal-settings`;
    const response = await this.fetchWithAuth(url, {
      method: 'PUT',
      body: JSON.stringify(model),
    });
    return await this.getJsonResponse(response);
  }

  public async getCompanyStatus(): Promise<CompanyStatusDto> {
    const url = `${COMPANIES_URL}/status`;
    const response = await this.fetchWithAuth(url);
    return this.getJsonResponse(response);
  }

  public async getCompanyCanBeCharged(): Promise<EligibleDto> {
    const url = `${COMPANIES_URL}/invoices/check`;
    const response = await this.fetchWithAuth(url);
    return this.getJsonResponse(response);
  }

  public async checkCompanySelfManaged(): Promise<SelfManagedFlagDto> {
    const url = `${COMPANIES_URL}/self-managed`;
    const response = await this.fetchWithAuth(url);
    return this.getJsonResponse(response);
  }

  public async updateCompanySelfManaged(model: ResolveSelfManagedDto): Promise<SelfManagedFlagDto> {
    const url = `${COMPANIES_URL}/self-managed`;
    const response = await this.fetchWithAuth(url, {
      method: 'PUT',
      body: JSON.stringify(model),
    });
    return this.getJsonResponse(response);
  }

  public async getUnassignedBankAccounts(): Promise<UnassignedBankAccountDto[]> {
    const url = `${COMPANIES_URL}/self-managed/unassigned-bank-accounts`;
    const response = await this.fetchWithAuth(url);
    return this.getJsonResponse(response);
  }

  public async requestTenantAchNumberLinking(): Promise<void> {
    const url = `${COMPANIES_URL}/request-tenant-ach-number-linking`;
    await this.fetchWithAuth(url, {
      method: 'POST',
    });
  }
}

export const companyRepository = new CompaniesRepository();
